/**
 * VERSION SELECTOR
 * 
 * Componente para alternar entre versão Master (original) e Editável da DRE
 * 
 * Features:
 * - Toggle entre master/editable
 * - Indicador visual da versão ativa
 * - Tooltip explicativo
 * - Responsive (mobile-friendly)
 * 
 * @version 1.0.0
 */

import {
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  Tooltip,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Lock, Edit } from '@mui/icons-material';
import type { VersionType } from '../../types/dre';

// ============================================================================
// TYPES
// ============================================================================

export interface VersionSelectorProps {
  /** Versão ativa atual */
  activeVersion: VersionType;
  /** Callback ao mudar versão */
  onVersionChange: (version: VersionType) => void;
  /** Desabilitado? */
  disabled?: boolean;
  /** Mostrar labels? */
  showLabels?: boolean;
  /** Tamanho */
  size?: 'small' | 'medium' | 'large';
  /** Orientação */
  orientation?: 'horizontal' | 'vertical';
}

// ============================================================================
// CONFIG
// ============================================================================

const VERSION_CONFIG = {
  master: {
    label: 'Master',
    description: 'Versão original (somente leitura)',
    icon: Lock,
    color: 'info.main', // blue
    bgColor: 'rgba(33, 150, 243, 0.08)',
  },
  editable: {
    label: 'Editável',
    description: 'Versão customizável para análise',
    icon: Edit,
    color: 'warning.main', // orange
    bgColor: 'rgba(255, 152, 0, 0.08)',
  },
} as const;

// ============================================================================
// COMPONENT
// ============================================================================

export function VersionSelector({
  activeVersion,
  onVersionChange,
  disabled = false,
  showLabels = true,
  size = 'medium',
  orientation = 'horizontal',
}: VersionSelectorProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (_event: React.MouseEvent<HTMLElement>, newVersion: VersionType | null) => {
    console.log('[VersionSelector] 🔄 Mudança solicitada:', {
      anterior: activeVersion,
      nova: newVersion,
    });
    
    if (newVersion !== null) {
      onVersionChange(newVersion);
      console.log('[VersionSelector] ✅ Versão mudada para:', newVersion);
    }
  };

  // Mobile força layout compacto
  const effectiveSize = isMobile ? 'small' : size;

  // Config da versão ativa
  const activeConfig = VERSION_CONFIG[activeVersion as keyof typeof VERSION_CONFIG];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        bgcolor: 'background.default',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <ToggleButtonGroup
        value={activeVersion}
        exclusive
        onChange={handleChange}
        disabled={disabled}
        size={effectiveSize}
        orientation={orientation}
        sx={{
          width: orientation === 'vertical' ? '100%' : 'auto',
          '& .MuiToggleButton-root': {
            px: effectiveSize === 'small' ? 1.5 : 2,
            py: effectiveSize === 'small' ? 0.75 : 1,
            textTransform: 'none',
            fontWeight: 600,
            minWidth: effectiveSize === 'small' ? 100 : 120,
            '&.Mui-selected': {
              bgcolor: activeConfig.bgColor,
              color: activeConfig.color,
              '&:hover': {
                bgcolor: activeConfig.bgColor,
              },
            },
          },
        }}
      >
        {/* Master */}
        <Tooltip
          title={VERSION_CONFIG.master.description}
          arrow
          placement={orientation === 'horizontal' ? 'top' : 'right'}
        >
          <ToggleButton 
            value="master"
            data-testid="version-selector-master"
            aria-label="Versão Master (original)"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Lock fontSize="small" />
              {showLabels && <span>{VERSION_CONFIG.master.label}</span>}
            </Box>
          </ToggleButton>
        </Tooltip>

        {/* Editável */}
        <Tooltip
          title={VERSION_CONFIG.editable.description}
          arrow
          placement={orientation === 'horizontal' ? 'top' : 'right'}
        >
          <ToggleButton 
            value="editable"
            data-testid="version-selector-editable"
            aria-label="Versão Editável (customizável)"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Edit fontSize="small" />
              {showLabels && <span>{VERSION_CONFIG.editable.label}</span>}
            </Box>
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Paper>
  );
}

/**
 * Versão compacta - apenas ícones
 */
export function VersionSelectorCompact({
  activeVersion,
  onVersionChange,
  disabled = false,
}: Pick<VersionSelectorProps, 'activeVersion' | 'onVersionChange' | 'disabled'>) {
  return (
    <VersionSelector
      activeVersion={activeVersion}
      onVersionChange={onVersionChange}
      disabled={disabled}
      showLabels={false}
      size="small"
    />
  );
}

/**
 * Versão com descrição expandida
 */
export function VersionSelectorExpanded({
  activeVersion,
  onVersionChange,
  disabled = false,
}: Pick<VersionSelectorProps, 'activeVersion' | 'onVersionChange' | 'disabled'>) {
  const config = VERSION_CONFIG[activeVersion as keyof typeof VERSION_CONFIG];
  const Icon = config.icon;

  return (
    <Box>
      {/* Selector */}
      <VersionSelector
        activeVersion={activeVersion}
        onVersionChange={onVersionChange}
        disabled={disabled}
        showLabels={true}
        size="medium"
      />

      {/* Descrição da versão ativa */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 1,
          bgcolor: config.bgColor,
          border: `1px solid ${config.color}33`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Icon sx={{ fontSize: 20, color: config.color }} />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: config.color,
            }}
          >
            {config.label}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {config.description}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Versão inline com texto
 */
export function VersionSelectorInline({
  activeVersion,
  onVersionChange,
  disabled = false,
  label = 'Versão DRE:',
}: Pick<VersionSelectorProps, 'activeVersion' | 'onVersionChange' | 'disabled'> & {
  label?: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <VersionSelector
        activeVersion={activeVersion}
        onVersionChange={onVersionChange}
        disabled={disabled}
        showLabels={true}
        size="small"
      />
    </Box>
  );
}
